import { useEffect, useRef, useState } from "react";

// Editable 2D line/curve graph for 1D tables. Fills its parent (resizable).
// Drag any point up/down to change its value; values sync back via onChange.
export function MapCurve2D({ values, xLabels, onChange, onEditStart, yTitle = "Value", xTitle = "Index", orig = null }) {
  const boxRef = useRef(null);
  const svgRef = useRef(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [hoverIdx, setHoverIdx] = useState(null);
  const [dim, setDim] = useState({ w: 760, h: 360 });

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setDim({ w: Math.max(320, el.clientWidth), h: Math.max(220, el.clientHeight) }));
    ro.observe(el); setDim({ w: Math.max(320, el.clientWidth), h: Math.max(220, el.clientHeight) });
    return () => ro.disconnect();
  }, []);

  const n = values.length;
  const W = dim.w, H = dim.h;
  const m = { l: 74, r: 34, t: 26, b: 52 };
  const pw = W - m.l - m.r, ph = H - m.t - m.b;
  const hasOrig = orig && orig.length === n;
  const rangeVals = hasOrig ? values.concat(orig) : values;
  const vmin = Math.min(...rangeVals), vmax = Math.max(...rangeVals);
  const pad = (vmax - vmin) * 0.15 || Math.abs(vmax) * 0.1 || 1;
  const dMin = vmin - pad, dMax = vmax + pad;
  const X = (i) => m.l + (n <= 1 ? pw / 2 : (i / (n - 1)) * pw);
  const Y = (v) => m.t + ph - ((v - dMin) / (dMax - dMin)) * ph;
  const yToVal = (py) => dMin + ((m.t + ph - py) / ph) * (dMax - dMin);
  const color = (v) => { const t = vmax === vmin ? 0.5 : (v - vmin) / (vmax - vmin); return `hsl(${Math.round((1 - t) * 190)},78%,52%)`; };

  const pointerVal = (e) => { const p = e.touches ? e.touches[0] : e; const rect = svgRef.current.getBoundingClientRect(); const py = (p.clientY - rect.top) * (H / rect.height); return Math.round(yToVal(py) * 100) / 100; };
  const onMove = (e) => { if (dragIdx == null) return; if (e.touches && e.cancelable) e.preventDefault(); const v = pointerVal(e); const nv = values.slice(); nv[dragIdx] = v; onChange(nv); };

  const yticks = Array.from({ length: 5 }, (_, i) => dMin + ((dMax - dMin) * i) / 4);
  const fmt = (v) => (Number.isInteger(v) ? v : Math.round(v * 10) / 10);
  const stepX = Math.max(1, Math.ceil(n / 20));

  return (
    <div ref={boxRef} className="w-full h-full">
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="w-full h-full select-none" data-testid="mapedit-curve" style={{ touchAction: "none" }}
        onMouseMove={onMove} onMouseUp={() => setDragIdx(null)} onMouseLeave={() => setDragIdx(null)}
        onTouchMove={onMove} onTouchEnd={() => setDragIdx(null)} onTouchCancel={() => setDragIdx(null)}>
        {yticks.map((v, i) => (
          <g key={i}>
            <line x1={m.l} y1={Y(v)} x2={W - m.r} y2={Y(v)} stroke="#1e293b" strokeDasharray="3 3" />
            <text x={m.l - 8} y={Y(v) + 3} textAnchor="end" fontSize="11" fill="#93c5fd" fontFamily="monospace">{fmt(v)}</text>
          </g>
        ))}
        {values.map((_, i) => (i % stepX === 0 || i === n - 1) && (
          <text key={i} x={X(i)} y={H - m.b + 18} textAnchor="middle" fontSize="10" fill="#64748b" fontFamily="monospace">{xLabels && xLabels[i] != null ? xLabels[i] : i + 1}</text>
        ))}
        <text x={W / 2} y={H - 6} textAnchor="middle" fontSize="11" fontWeight="bold" fill="#ef4444" fontFamily="monospace">{xTitle}</text>
        <text x={16} y={H / 2} textAnchor="middle" fontSize="11" fontWeight="bold" fill="#ef4444" fontFamily="monospace" transform={`rotate(-90 16 ${H / 2})`}>{yTitle}</text>
        {hasOrig && (
          <g data-testid="mapedit-curve-orig">
            <polyline fill="none" stroke="#4ade80" strokeWidth="1.5" strokeDasharray="5 4" opacity="0.85" points={orig.map((v, i) => `${X(i)},${Y(v)}`).join(" ")} />
            {orig.map((v, i) => (values[i] !== v ? <circle key={i} cx={X(i)} cy={Y(v)} r={3.5} fill="#4ade80" opacity="0.9" /> : null))}
          </g>
        )}
        <polyline fill="none" stroke="#e2e8f0" strokeWidth="1.5" points={values.map((v, i) => `${X(i)},${Y(v)}`).join(" ")} />
        {values.map((v, i) => (
          <g key={i}>
            <circle cx={X(i)} cy={Y(v)} r={dragIdx === i || hoverIdx === i ? 7 : 5} fill={color(v)} stroke="#fff" strokeWidth={dragIdx === i ? 2 : 1}
              style={{ cursor: "ns-resize", touchAction: "none" }} data-testid={`mapedit-curve-pt-${i}`}
              onMouseDown={() => { onEditStart && onEditStart(); setDragIdx(i); }}
              onTouchStart={() => { onEditStart && onEditStart(); setDragIdx(i); }}
              onMouseEnter={() => setHoverIdx(i)} onMouseLeave={() => setHoverIdx(null)} />
            {(dragIdx === i || hoverIdx === i) && (<text x={X(i)} y={Y(v) - 12} textAnchor="middle" fontSize="11" fontWeight="bold" fill="#fff" fontFamily="monospace">{fmt(v)}</text>)}
          </g>
        ))}
      </svg>
    </div>
  );
}
